
import React from 'react';

// --- SHARED PROP TYPE ---
export type IconProps = { className?: string };

// --- WRAPPERS ---

export const IconWrapper: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className = "" }) => (
    <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl backdrop-blur-md bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center text-yellow-400 transition-all duration-300 shadow-lg group-hover:shadow-yellow-400/20 group-hover:scale-110 group-hover:border-yellow-400/40 group-hover:from-white/15 group-hover:to-white/10 ${className}`}>
        {children}
    </div>
);

export const RedIconWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl backdrop-blur-md bg-gradient-to-br from-red-500/20 to-red-500/5 border border-red-500/20 flex items-center justify-center text-red-400 transition-all duration-300 shadow-lg group-hover:shadow-red-500/20 group-hover:scale-110 group-hover:border-red-500/40 group-hover:from-red-500/25 group-hover:to-red-500/10">
        {children}
    </div>
);

// --- HELPER ---
const DuotoneSvg: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-8 w-8 sm:h-9 sm:w-9"} viewBox="0 0 24 24" fill="none">
        {children}
    </svg>
);

export const SmallIcon: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-5 w-5"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        {children}
    </svg>
);

// --- TAB ICONS ---
export const ClinicalTabIcon: React.FC<IconProps> = ({ className }) => (
    <SmallIcon className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></SmallIcon>
);
export const RetailTabIcon: React.FC<IconProps> = ({ className }) => (
    <SmallIcon className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></SmallIcon>
);
export const ReferenceTabIcon: React.FC<IconProps> = ({ className }) => (
    <SmallIcon className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></SmallIcon>
);


// --- CLINICAL TOOLS ---

export const EmergencyGuideIcon: React.FC<IconProps> = ({ className }) => (
    <RedIconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 22V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50" />
        </DuotoneSvg>
    </RedIconWrapper>
);

export const AnalyzeRxIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="15" cy="15" r="5" className="text-yellow-400 opacity-20" fill="currentColor" />
            <path d="M18.5 18.5L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </DuotoneSvg>
    </IconWrapper>
);

export const AntibioticsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <circle cx="12" cy="12" r="10" className="opacity-20" fill="currentColor"/>
            <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="16" cy="8" r="2" className="opacity-40" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const AnticholinergicBurdenIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 2a10 10 0 100 20 10 10 0 000-20z" className="opacity-20" fill="currentColor"/>
            <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 15a3 3 0 106 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const AnticoagBridgingIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" className="opacity-20" fill="currentColor" />
            <path d="M7 12h10M12 7v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </DuotoneSvg>
    </IconWrapper>
);

export const ClinicalCriteriaIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <rect x="4" y="4" width="16" height="16" rx="2" className="opacity-20" fill="currentColor"/>
            <path d="M9 9h6M9 13h6M9 17h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M16 4v2a2 2 0 01-2 2h-4a2 2 0 01-2-2V4" stroke="currentColor" strokeWidth="2" />
        </DuotoneSvg>
    </IconWrapper>
);

export const ClinicalTrialsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001.51 1H15.5a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" className="opacity-20" fill="currentColor"/>
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const DietaryGuideIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 2a8 8 0 00-8 8c0 5.4 6.6 10.6 7.4 11.3a1 1 0 001.2 0c.8-.7 7.4-5.9 7.4-11.3a8 8 0 00-8-8z" className="opacity-20" fill="currentColor"/>
            <path d="M12 2v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 14l-2-2m2 2l2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const DoseAdjustIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            {/* Slider tracks */}
            <path d="M4 8h16M4 16h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="opacity-30" />
            {/* Knobs */}
            <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="2" fill="currentColor" className="text-white/20" />
            <circle cx="16" cy="16" r="3" stroke="currentColor" strokeWidth="2" fill="currentColor" className="text-white/20" />
            {/* Active indicator */}
            <path d="M8 8h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="2 2" />
        </DuotoneSvg>
    </IconWrapper>
);

export const DrugTaperingIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M22 19H2L11 2l11 17z" className="opacity-10" fill="currentColor"/>
            <path d="M2 20h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M6 16h12M8 12h8M10 8h4M12 4v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const FormularyAnalyzerIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" className="opacity-20" fill="currentColor"/>
            <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="opacity-50"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const GuidelinesIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" className="opacity-20" fill="currentColor"/>
            <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 13H8m8 4H8m2-8H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const HIVInfoIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
             <path d="M12 4C14.7614 4 17 6.23858 17 9C17 13.5 12 20 12 20C12 20 7 13.5 7 9C7 6.23858 9.23858 4 12 4Z" className="opacity-20" fill="currentColor" />
             <path d="M12 14C13.5 12 16 10.5 16 8.5C16 6.2 14.2 4.5 12 4.5C9.8 4.5 8 6.2 8 8.5C8 10.5 10.5 12 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
             <path d="M12 14L8.5 20.5M12 14L15.5 20.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const INRInfoIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" className="opacity-20" fill="currentColor"/>
            <path d="M12 7v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="16" r="1" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const InsulinToolIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 3v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <rect x="9" y="6" width="6" height="12" rx="1" className="opacity-20" fill="currentColor"/>
            <path d="M9 10h6" stroke="currentColor" strokeWidth="2"/>
            <path d="M9 14h6" stroke="currentColor" strokeWidth="2"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const InteractionsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <circle cx="9" cy="12" r="6" className="opacity-30" fill="currentColor"/>
            <circle cx="15" cy="12" r="6" className="opacity-30" fill="currentColor"/>
            <circle cx="9" cy="12" r="6" stroke="currentColor" strokeWidth="2"/>
            <circle cx="15" cy="12" r="6" stroke="currentColor" strokeWidth="2"/>
            {/* Intersection area hint */}
            <path d="M12 9a4 4 0 0 1 0 6 4 4 0 0 1 0-6" fill="currentColor" className="text-white opacity-50" />
        </DuotoneSvg>
    </IconWrapper>
);

export const IVCompatibilityIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 2v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 8l-5 8h10l-5-8z" className="opacity-20" fill="currentColor"/>
            <path d="M12 8l-5 8h10l-5-8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
            <path d="M12 16v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const MissedDoseIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <circle cx="12" cy="12" r="10" className="opacity-20" fill="currentColor"/>
            <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="1" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const MMECalculatorIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            {/* Base of scale */}
            <path d="M12 3v18M8 21h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            {/* Crossbar */}
            <path d="M4 7h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            {/* Pans */}
            <path d="M4 7v5a3 3 0 0 0 3 3h0a3 3 0 0 0 3-3V7" stroke="currentColor" strokeWidth="2" fill="currentColor" className="opacity-20" />
            <path d="M14 7v5a3 3 0 0 0 3 3h0a3 3 0 0 0 3-3V7" stroke="currentColor" strokeWidth="2" fill="currentColor" className="opacity-20" />
        </DuotoneSvg>
    </IconWrapper>
);

export const MTMCMRToolIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="14" r="3" stroke="currentColor" strokeWidth="2" className="opacity-50" />
            <path d="M16 18l-2-2" stroke="currentColor" strokeWidth="2" />
        </DuotoneSvg>
    </IconWrapper>
);

export const PatientCounselingIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
             <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" className="opacity-20" fill="currentColor" />
             <path d="M8 11h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
             <path d="M12 7v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
             <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const PediatricGuideIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <circle cx="12" cy="7" r="4" className="opacity-20" fill="currentColor"/>
            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
            <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const PGxGuideIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" className="opacity-20" fill="currentColor"/>
            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 2a10 10 0 00-10 10 10 10 0 0010 10 10 10 0 0010-10A10 10 0 0012 2zm0 18a8 8 0 110-16 8 8 0 010 16z" stroke="currentColor" strokeWidth="2" className="opacity-50"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const PharmacokineticsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M3 20h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M3 20c5-12 9-16 18-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 20l18-2v2H3z" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const TherapeuticInterchangeIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="2" y="10" width="20" height="4" rx="2" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const VaccineToolIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M14 4l2 2m-9 9l-5 5 2 2 5-5m4-4l-6 6m9-11l-1-1a2 2 0 00-2.828 0l-1.414 1.414a2 2 0 000 2.828l1 1m4-4l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="9" y="7" width="6" height="10" transform="rotate(-45 12 12)" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

// --- RETAIL TOOLS ---

export const CompoundingCalculatorIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M19 3h-4a2 2 0 00-2 2v12a2 2 0 002 2h4a2 2 0 002-2V5a2 2 0 00-2-2zm-14 0h4a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" className="opacity-20" fill="currentColor"/>
            <path d="M15 21h-6a2 2 0 01-2-2v-6h10v6a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 3v10" stroke="currentColor" strokeWidth="2"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const CSReferenceIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="8" y="11" width="8" height="10" rx="1" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const DaysSupplyIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" className="opacity-20" fill="currentColor"/>
            <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const DrugPriceFinderIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <circle cx="12" cy="12" r="10" className="opacity-20" fill="currentColor"/>
            <path d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M12 12h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const FederalLawIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="9" y="3" width="6" height="2" className="opacity-50" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const OTCFinderIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 9h4" stroke="currentColor" strokeWidth="2" className="opacity-50"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const PAAssistantIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="9" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const PillIdentifierIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <circle cx="11" cy="11" r="8" className="opacity-20" fill="currentColor"/>
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M11 8v6M8 11h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const SigCodesIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" className="opacity-20" fill="currentColor"/>
            <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const StateLawReferenceIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M3 21v-8a2 2 0 012-2h14a2 2 0 012 2v8H3z" className="opacity-20" fill="currentColor"/>
            <path d="M12 3l9 5v13H3V8l9-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
            <path d="M12 13a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2"/>
        </DuotoneSvg>
    </IconWrapper>
);

// --- REFERENCE TOOLS ---

export const AntidotesIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" className="opacity-20" fill="currentColor"/>
            <path d="M12 15v-4m0-4h.01M12 7a5 5 0 100 10 5 5 0 000-10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 22c4-4 7.5-8 7.5-11.5a7.5 7.5 0 00-15 0c0 3.5 3.5 7.5 7.5 11.5z" stroke="currentColor" strokeWidth="2"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const BiostatsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" className="opacity-20" fill="currentColor"/>
            <path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M2 20h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const CalculatorsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <rect x="4" y="3" width="16" height="18" rx="2" className="opacity-20" fill="currentColor"/>
            <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 7h8M8 11h8M8 15h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const CannabinoidsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                {/* Stem */}
                <path d="M12 22v-8" />
                {/* Central Leaf */}
                <path d="M12 14c0-4 0-9 0-12 0 3 2.5 8 2.5 10S12.5 14 12 14z" fill="currentColor" className="opacity-40" />
                <path d="M12 14c0-4 0-9 0-12 0 3 2.5 8 2.5 10S12.5 14 12 14z" />
                {/* Middle Leaves */}
                <path d="M12 14c2-1 7-4.5 9-6-1 3.5-4 7-6.5 8-1 0.5-2.5-1-2.5-2z" fill="currentColor" className="opacity-40" />
                <path d="M12 14c2-1 7-4.5 9-6-1 3.5-4 7-6.5 8-1 0.5-2.5-1-2.5-2z" />
                <path d="M12 14c-2-1-7-4.5-9-6 1 3.5 4 7 6.5 8 1 0.5 2.5-1 2.5-2z" fill="currentColor" className="opacity-40" />
                <path d="M12 14c-2-1-7-4.5-9-6 1 3.5 4 7 6.5 8 1 0.5 2.5-1 2.5-2z" />
                {/* Bottom Leaves */}
                <path d="M12 14c1.5 1.5 4.5 3.5 7 3.5-1.5 1-3.5 1-5 0s-2-2-2-3.5z" fill="currentColor" className="opacity-40" />
                <path d="M12 14c1.5 1.5 4.5 3.5 7 3.5-1.5 1-3.5 1-5 0s-2-2-2-3.5z" />
                <path d="M12 14c-1.5 1.5-4.5 3.5-7 3.5 1.5 1 3.5 1 5 0s2-2 2-3.5z" fill="currentColor" className="opacity-40" />
                <path d="M12 14c-1.5 1.5-4.5 3.5-7 3.5 1.5 1 3.5 1 5 0s2-2 2-3.5z" />
            </g>
        </DuotoneSvg>
    </IconWrapper>
);

export const CTTrackerIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 14l9-5-9-5-9 5 9 5z" className="opacity-20" fill="currentColor"/>
            <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const ChemoManIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <circle cx="12" cy="4" r="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 6v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M9 10h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M9 18l3-4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="10" r="6" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const CompoundingFormulasIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 14h4" className="opacity-50" stroke="currentColor" strokeWidth="2"/>
            <path d="M9 17h6" className="opacity-50" stroke="currentColor" strokeWidth="2"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const ConversionsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="2" y="10" width="20" height="4" rx="1" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const DrugShortageTrackerIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 3v4l-2 1 2 1 2-1-2-1zm0 8v10" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const LabChartsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="4" y="4" width="16" height="16" rx="2" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const MnemonicsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="4" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const VitaminsIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="5" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

// --- CORE TOOLS (FOR TOOLS.TSX) ---

export const DrugIndexIcon: React.FC<IconProps> = ({ className }) => (
   <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 9h3M6 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M16.5 5c1.747 0 3.332.477 4.5 1.253v13" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const ClinicalGuidesIcon: React.FC<IconProps> = ({ className }) => (
   <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="5" y="7" width="14" height="14" rx="2" className="opacity-20" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const StudyModeIcon: React.FC<IconProps> = ({ className }) => (
   <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="4" className="opacity-30" fill="currentColor"/>
            <path d="M12 10v4" stroke="currentColor" strokeWidth="2"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const EvidenceSearchIcon: React.FC<IconProps> = ({ className }) => (
   <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="10" cy="10" r="7" className="opacity-20" fill="currentColor"/>
            <path d="M10 7v3h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const AllToolsIcon: React.FC<IconProps> = ({ className }) => (
   <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
             <rect x="4" y="4" width="4" height="4" rx="1" className="opacity-30" fill="currentColor"/>
             <rect x="14" y="14" width="4" height="4" rx="1" className="opacity-30" fill="currentColor"/>
        </DuotoneSvg>
    </IconWrapper>
);

// --- NEW TOOL ICONS ---

export const FormsFinderIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" className="opacity-20" fill="currentColor"/>
            <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="14" r="3" stroke="currentColor" strokeWidth="2"/>
            <path d="M16 18l-2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const FitnessGuideIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <rect x="2" y="9" width="20" height="6" rx="2" className="opacity-20" fill="currentColor"/>
            <path d="M2 12h2m16 0h2M6 12H4m16 0h-2m-5-7v14m-3-7h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);

export const PregnancyLactationIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
            <path d="M12 13c-2.76 0-5 1.79-5 4v3h10v-3c0-2.21-2.24-4-5-4z" className="opacity-20" fill="currentColor"/>
            <path d="M12 13c-2.76 0-5 1.79-5 4v3h10v-3c0-2.21-2.24-4-5-4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </DuotoneSvg>
    </IconWrapper>
);

export const GeriatricGuideIcon: React.FC<IconProps> = ({ className }) => (
    <IconWrapper>
        <DuotoneSvg className={className}>
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" className="opacity-20" fill="currentColor"/>
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" stroke="currentColor" strokeWidth="2"/>
            <path d="M18 10v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </DuotoneSvg>
    </IconWrapper>
);
